/**
 * Figura principal (comissao geral) e a grade de totais da Central, cada
 * um com a comparacao contra o periodo anterior.
 *
 * Decisoes nao obvias:
 *  1. UM so `emphasis` na pagina, e e a comissao: e o faturamento do Converte
 *     Facil, o numero que a administracao abre a Central para ver. A margem
 *     aparece na linha de apoio dele, porque e a segunda pergunta.
 *  2. A taxa media e PONDERADA (recuperados / abandonados de todas as
 *     empresas). Media simples das taxas daria o mesmo peso a uma empresa de
 *     10 carrinhos e a outra de 400.
 *  3. Nao usa o `KpiCard` compartilhado na grade porque ele nao tem linha de
 *     delta; `CartaoTotal` repete o visual dele (contorno, glifo, rotulo) e
 *     acrescenta a variacao com glifo + texto. O numero grande continua sem
 *     cor de serie.
 *  4. Custo sobe junto com o volume trabalhado, entao o delta dele e
 *     "neutro"; estorno subir e ruim ("menor_melhor"); o resto, "maior_melhor".
 *  5. Nenhum valor chega em centavos a um cartao sem passar por lib/format:
 *     o cartao so recebe texto pronto.
 *  6. Custo e margem `null` (motor real, custo ainda nao medido) aparecem como
 *     "—" com a frase que explica por que, e SEM delta: nunca como R$ 0,00.
 */

import { KpiCard } from '../../../components/KpiCard';
import { Sparkline } from '../../../components/Sparkline';
import { Icon, type IconName } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatCountCompact, formatPercentFromBps, VALOR_AUSENTE } from '../../../lib/format';
import { porUnidade } from '../comparar';
import type { PlatformTotals } from '../types';
import { COST_CHANNEL_SHORT } from '../vocabulario';
import { Delta } from './Delta';

type TomDoIcone = 'brand' | 'accent' | 'good' | 'info' | 'critical' | 'neutral';

const ICONE_TOM: Readonly<Record<TomDoIcone, string>> = {
  brand: 'bg-brand/10 text-brand',
  accent: 'bg-accent/10 text-accent',
  good: 'bg-status-good/10 text-status-good',
  info: 'bg-info/10 text-info',
  critical: 'bg-status-critical/10 text-status-critical',
  neutral: 'bg-ink/5 text-ink-muted',
};

function CartaoTotal({
  icon,
  tom,
  label,
  dica,
  value,
  delta,
  support,
}: {
  readonly icon: IconName;
  readonly tom: TomDoIcone;
  readonly label: string;
  /** Explicacao em termos de negocio; vira `title` do rotulo. */
  readonly dica: string;
  readonly value: string;
  readonly delta: JSX.Element | null;
  readonly support: string;
}): JSX.Element {
  return (
    <article className="flex flex-col gap-2 rounded-2xl border border-line bg-surface px-5 py-4 shadow-soft transition-shadow hover:shadow-float">
      <h3 className="flex items-center gap-2 text-sm font-medium text-ink-muted" title={dica}>
        <span aria-hidden="true" className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg ${ICONE_TOM[tom]}`}>
          <Icon name={icon} size="sm" />
        </span>
        <span className="min-w-0 truncate">{label}</span>
        <Icon name="info" size="xs" className="shrink-0 text-ink-subtle" />
      </h3>
      <p className="text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{value}</p>
      {delta}
      <p className="text-xs text-ink-subtle">{support}</p>
    </article>
  );
}

export function ComissaoGeral({
  totals,
  anterior,
  tendenciaComissao,
  rotuloTendencia,
  periodo,
}: {
  readonly totals: PlatformTotals;
  readonly anterior: PlatformTotals | null;
  /** Comissao por balde (dia ou semana), para o traco de tendencia. */
  readonly tendenciaComissao: readonly number[];
  readonly rotuloTendencia: string;
  /** Descricao da janela do cabecalho ("Ultimos 30 dias"). */
  readonly periodo: string;
}): JSX.Element {
  const vendas = totals.recoveredCount === 1 ? 'venda recuperada' : 'vendas recuperadas';
  const margem =
    totals.marginCents === null
      ? 'custo da operação ainda não medido, sem margem calculada'
      : totals.marginBps === null
        ? 'sem comissão no período, os custos fixos correm mesmo assim'
        : `margem de ${formatBRLFromCents(totals.marginCents)} (${formatPercentFromBps(totals.marginBps)}) depois dos custos`;

  return (
    <KpiCard
      emphasis
      label="Comissão geral do período"
      value={formatBRLFromCents(totals.commissionCents)}
      support={`Soma da comissão congelada em cada venda recuperada (ADR-001) · ${formatCount(totals.recoveredCount)} ${vendas} · ${margem} · ${periodo}`}
      icon={<Icon name="comissao" size="sm" />}
      aside={
        <div className="flex flex-col items-end gap-2">
          <Sparkline values={tendenciaComissao} label={rotuloTendencia} className="h-12 w-36 sm:h-14 sm:w-52" />
          <Delta atual={totals.commissionCents} anterior={anterior === null ? null : anterior.commissionCents} tipo="dinheiro" monocromatico className="text-white" />
        </div>
      }
    />
  );
}

export function GradeDeTotais({ totals, anterior }: { readonly totals: PlatformTotals; readonly anterior: PlatformTotals | null }): JSX.Element {
  const ant = anterior;
  const custo = totals.costByChannel;
  const ticket = porUnidade(totals.recoveredAmountCents, totals.recoveredCount);
  const taxaAnterior = ant === null || ant.abandonedCount === 0 ? null : ant.recoveryRateBps;

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <CartaoTotal
        icon="moeda"
        tom="brand"
        label="Recuperado geral (bruto)"
        dica="Valor pago pelos leads que voltaram depois do abandono ou do vencimento, em todas as empresas. É a base da comissão."
        value={formatBRLFromCents(totals.recoveredAmountCents)}
        delta={<Delta atual={totals.recoveredAmountCents} anterior={ant === null ? null : ant.recoveredAmountCents} tipo="dinheiro" />}
        support="Valor pago pelos leads recuperados, todas as empresas"
      />
      <CartaoTotal
        icon="atualizar"
        tom="good"
        label="Vendas recuperadas"
        dica="Pagamentos aprovados em até 7 dias depois de um contato, creditados ao último contato (ADR-001)."
        value={formatCountCompact(totals.recoveredCount)}
        delta={<Delta atual={totals.recoveredCount} anterior={ant === null ? null : ant.recoveredCount} tipo="contagem" />}
        support={ticket === null ? 'Nenhuma venda recuperada no período' : `Ticket médio de ${formatBRLFromCents(ticket)} por venda`}
      />
      <CartaoTotal
        icon="empresa"
        tom="info"
        label="Líquido das empresas"
        dica="Recuperado bruto menos a comissão do Converte Fácil: o que ficou com as empresas. É o retorno que elas veem no CRM."
        value={formatBRLFromCents(totals.netAmountCents)}
        delta={<Delta atual={totals.netAmountCents} anterior={ant === null ? null : ant.netAmountCents} tipo="dinheiro" />}
        support="Recuperado bruto menos a comissão: o que fica com as empresas"
      />
      <CartaoTotal
        icon="grafico"
        tom="info"
        label="Taxa média de recuperação"
        dica="Recuperados sobre entradas (abandonos e vencimentos) de todas as empresas juntas, ponderada pelo volume de cada uma."
        value={totals.abandonedCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(totals.recoveryRateBps)}
        delta={<Delta atual={totals.recoveryRateBps} anterior={taxaAnterior} tipo="bps" />}
        support={`${formatCount(totals.recoveredCount)} de ${formatCount(totals.abandonedCount)} entradas, ponderada pelo volume`}
      />
      <CartaoTotal
        icon="raio"
        tom="accent"
        label="Custos da operação"
        dica="Voz (minutos na Vapi), WhatsApp (conversas na Meta ou instância na Evolution), e-mail (por mil) e infra (servidor e banco rateados). Tarifas de exemplo na prévia."
        value={totals.costCents === null ? VALOR_AUSENTE : formatBRLFromCents(totals.costCents)}
        delta={totals.costCents === null ? null : <Delta atual={totals.costCents} anterior={ant === null ? null : ant.costCents} tipo="dinheiro" sentido="neutro" />}
        support={
          custo === null
            ? 'Custo ainda não medido: o motor não lê as faturas de voz, WhatsApp e infra.'
            : `${COST_CHANNEL_SHORT.voice} ${formatBRLFromCents(custo.voiceCents)} · ${COST_CHANNEL_SHORT.whatsapp} ${formatBRLFromCents(custo.whatsappCents)} · ${COST_CHANNEL_SHORT.email} ${formatBRLFromCents(custo.emailCents)} · ${COST_CHANNEL_SHORT.infra} ${formatBRLFromCents(custo.infraCents)}`
        }
      />
      <CartaoTotal
        icon="check"
        tom={totals.marginCents === null ? 'neutral' : totals.marginCents < 0 ? 'critical' : 'good'}
        label="Margem da plataforma"
        dica="Comissão menos custos da operação. É o que sobra para o Converte Fácil depois de pagar voz, WhatsApp, e-mail e infra."
        value={totals.marginCents === null ? VALOR_AUSENTE : formatBRLFromCents(totals.marginCents)}
        delta={totals.marginCents === null ? null : <Delta atual={totals.marginCents} anterior={ant === null ? null : ant.marginCents} tipo="dinheiro" />}
        support={
          totals.marginCents === null
            ? 'Sem custo medido não há margem: a comissão acima ainda não desconta voz, WhatsApp e infra.'
            : totals.marginBps === null
              ? 'Sem comissão no período; custos fixos correm mesmo assim'
              : `${formatPercentFromBps(totals.marginBps)} da comissão fica depois dos custos`
        }
      />
      <CartaoTotal
        icon="carrinho"
        tom="accent"
        label="Entradas no funil"
        dica="Checkouts que ficaram 30 minutos sem pagar e assinaturas que venceram, recebidos dos provedores. Todo toque parte de um destes fatos."
        value={formatCountCompact(totals.abandonedCount)}
        delta={<Delta atual={totals.abandonedCount} anterior={ant === null ? null : ant.abandonedCount} tipo="contagem" sentido="neutro" />}
        support="Abandonos e vencimentos já ocorridos, todas as empresas"
      />
      <CartaoTotal
        icon="alerta"
        tom={totals.reversedCount > 0 ? 'critical' : 'neutral'}
        label="Estornos"
        dica="Conversões revertidas por chargeback (ou reembolso, quando a política da empresa reverte). A comissão sai junto."
        value={formatCount(totals.reversedCount)}
        delta={<Delta atual={totals.reversedCount} anterior={ant === null ? null : ant.reversedCount} tipo="contagem" sentido="menor_melhor" />}
        support={totals.reversedCount === 0 ? 'Nenhuma conversão revertida no período' : `${formatBRLFromCents(totals.reversedAmountCents)} devolvidos · a comissão sai junto (ADR-001)`}
      />
    </div>
  );
}
